/**
 * Einführungs-Tutorial (Phase R+AA, Kapitel 21, Entscheidung #28/#36) +
 * iOS-Home-Bildschirm-Hinweis (Phase EE, Kapitel 21.7/9.1, Entscheidung #40).
 *
 * Automatischer Trigger: prüft nach dem Login, welcher der drei Zeitstempel
 * (`onboardingSeenBaseAt`/`onboardingSeenManagerAt`/`onboardingSeenChiefAt`)
 * für die AKTUELLE Rolle noch fehlt, und zeigt genau die dazu fehlenden
 * Karten (Basis-Satz 7 Karten + Willkommen/Los-geht's, danach kumulativ
 * Filialleitung +3, Chef +4 — Kapitel 21.4). Das greift automatisch auch
 * nach einer Beförderung, nicht nur beim allerersten Login (Entscheidung
 * #36) — DESHALB drei getrennte Felder statt einem: ein einzelner
 * Zeitstempel hätte eine spätere Beförderung nie erneut ausgelöst.
 *
 * Manueller Aufruf ("Tutorial erneut anzeigen", HomePage → Hilfe, Kapitel
 * 21.5): zeigt den VOLLSÄNDIGEN Stapel für die aktuelle Rolle, unabhängig
 * vom Seen-Status, und schreibt beim Schließen NICHTS zurück — reiner
 * Wiederholungs-Aufruf derselben Komponente, verändert die automatische
 * Trigger-Logik nicht (Kapitel 21.5).
 *
 * EE-Hintweis (21.7): rein clientseitig, KEIN Server-Zustand — erscheint
 * jedes Mal erneut, bis der Nutzer tatsächlich zum Home-Bildschirm
 * hinzugefügt hat. Läuft nur bei iOS/iPadOS außerhalb des Standalone-Modus,
 * und nur vor dem AUTOMATISCHEN Tutorial-Trigger (nicht bei "erneut
 * anzeigen" — dort hat der Nutzer die App ja bereits im Griff).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  useAuth,
  useIsAnyLocationManager,
  useCanManage,
  type AuthUser,
} from '../store/auth';
import { apiFetch } from '../lib/api';
import { isStandalone } from '../lib/platform';
import { hapticSuccess } from '../lib/haptics';

type Tier = 'base' | 'manager' | 'chief';

interface Card {
  title: string;
  points: string[];
}

const WELCOME_CARD: Card = {
  title: 'Willkommen bei ShiftSync',
  points: [
    'Eine kurze Einführung, was du mit deinem Zugang alles tun kannst.',
    'Du kannst jederzeit überspringen — nichts hier ist Pflicht.',
  ],
};

const DONE_CARD: Card = {
  title: 'Los geht\'s',
  points: ['Du kannst diese Einführung jederzeit über Profil → Hilfe erneut aufrufen.'],
};

/** Kapitel 21.4 — Basis-Satz, für JEDE Rolle (7 inhaltliche Karten). */
const BASE_CARDS: Card[] = [
  {
    title: 'Deine Schichten',
    points: [
      'Liste, Kalender und Lohn-Übersicht deiner Schichten.',
      'Ein- und Ausstempeln direkt aus der App.',
    ],
  },
  {
    title: 'Abwesenheiten',
    points: ['Urlaub, Frei und Krank beantragen.'],
  },
  {
    title: 'Mit Kolleg:innen tauschen oder einspringen',
    points: [
      'Geplante Schicht tauschen — auch standortübergreifend, wenn erlaubt.',
      'Spontan an jedem Standort deiner Firma einspringen, inkl. Zusatzstunden.',
    ],
  },
  {
    title: 'Übergabezeit verschieben',
    points: [
      'Start/Ende einer eigenen Schicht an der Grenze zu einer direkt angrenzenden Schicht anpassen.',
    ],
  },
  {
    title: 'Lohn & Auszahlung',
    points: [
      'Eigene Lohn-/Gehaltsdaten einsehen (nur Anzeige).',
      'Auszahlung bestätigen, Vorschuss melden.',
    ],
  },
  {
    title: 'Profil & Sicherheit',
    points: ['Benachrichtigungen, Passwort ändern, Geräte-/Sitzungsübersicht.'],
  },
];

/** Kapitel 21.4 — Zusatz-Satz Filialleitung (3 weitere Karten). */
const MANAGER_CARDS: Card[] = [
  {
    title: 'Schichtplan',
    points: [
      'Für deine(n) Standort(e) erstellen und jederzeit auch nach dem Veröffentlichen anpassen.',
      'Feiertags-, Deckungslücken- und Unterbesetzungs-Hinweise verstehen.',
    ],
  },
  {
    title: 'Team verwalten',
    points: [
      'Mitarbeiter anlegen, bearbeiten und bei Bedarf deaktivieren.',
      'Urlaubs-/Krankmeldungen deiner Standorte genehmigen oder ablehnen.',
      'Schicht-Vorlagen pflegen.',
    ],
  },
  {
    title: 'Team-Auswertung',
    points: [
      'Stunden- und, falls freigeschaltet, Lohnübersicht.',
      'Automatische Umbuchungen aus Schichttausch bei Bedarf rückgängig machen.',
    ],
  },
];

/** Kapitel 21.4 — Zusatz-Satz Chef (4 weitere Karten). */
const CHIEF_CARDS: Card[] = [
  {
    title: 'Standorte & Regionen',
    points: [
      'Standort-Stammdaten (Adresse, Filialpasswort, Öffnungszeiten, Bundesland).',
      'Die vier Standort-Rechte-Toggles, Regionen anlegen und zuordnen.',
    ],
  },
  {
    title: 'Firmenweite Verwaltung',
    points: [
      'Mitarbeiter zu Filialleitung befördern, Rollen firmenweit vergeben.',
      'Lohnrechnung firmenweit einsehen, Stundenlohn/Gehalt je Mitarbeiter festlegen.',
      'Beschäftigungsart und firmenweite Minijob-Grenze pflegen.',
    ],
  },
  {
    title: 'Entscheidungen bestätigen',
    points: ['Einspringen-Zusatzstunden sowie Vorschuss-Anfragen prüfen, bestätigen oder ablehnen.'],
  },
  {
    title: 'Firmen-Einstellungen',
    points: ['Z. B. Erinnerungsschwelle für unbestätigte Einspringen-Zusatzstunden.'],
  },
];

function isIosNonStandalone(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  return isIos && !isStandalone();
}

/** Baut den Karten-Stapel für die gegebenen Tiers (kumulativ, in fester Reihenfolge). */
function buildDeck(tiers: Tier[]): Card[] {
  const deck: Card[] = [];
  if (tiers.includes('base')) deck.push(WELCOME_CARD, ...BASE_CARDS);
  if (tiers.includes('manager')) deck.push(...MANAGER_CARDS);
  if (tiers.includes('chief')) deck.push(...CHIEF_CARDS);
  deck.push(DONE_CARD);
  return deck;
}

interface OnboardingTutorialProps {
  /** Manueller Aufruf ("erneut anzeigen") statt automatischem Trigger — schreibt beim Schließen nichts zurück. */
  manual?: boolean;
  onClose?: () => void;
}

/**
 * Bestimmt, welche Tiers für den AUTOMATISCHEN Trigger noch fehlen (Kapitel 21.3).
 * Reihenfolge wichtig: `base` zuerst, dann `manager`, dann `chief` — je nachdem,
 * ob die Rolle das jeweilige Tier überhaupt erreicht.
 */
function pendingAutoTiers(
  user: AuthUser,
  qualifiesManager: boolean,
  qualifiesChief: boolean,
): Tier[] {
  const tiers: Tier[] = [];
  if (!user.onboardingSeenBaseAt) tiers.push('base');
  if (qualifiesManager && !user.onboardingSeenManagerAt) tiers.push('manager');
  if (qualifiesChief && !user.onboardingSeenChiefAt) tiers.push('chief');
  return tiers;
}

export function OnboardingTutorial({ manual, onClose }: OnboardingTutorialProps) {
  const user = useAuth((s) => s.user);
  const token = useAuth((s) => s.token);
  const setUser = useAuth((s) => s.setUser);
  const qualifiesManager = useIsAnyLocationManager();
  const qualifiesChief = useCanManage();

  const [dismissedIosHint, setDismissedIosHint] = useState(false);
  const [index, setIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  // Gesamtprüfung 11.09.2026: schlägt das Speichern fehl (offline), bleibt
  // das automatische Tutorial trotzdem zu — sonst hing man darin fest
  // (onClose ist im Auto-Modus nicht gesetzt). Beim nächsten Start erneut.
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  const autoTiers = useMemo(
    () => (user ? pendingAutoTiers(user, qualifiesManager, qualifiesChief) : []),
    [user, qualifiesManager, qualifiesChief],
  );

  // Für den manuellen Aufruf: voller Stapel für die aktuelle Rolle, unabhängig vom Seen-Status.
  const manualTiers = useMemo<Tier[]>(() => {
    const tiers: Tier[] = ['base'];
    if (qualifiesManager) tiers.push('manager');
    if (qualifiesChief) tiers.push('chief');
    return tiers;
  }, [qualifiesManager, qualifiesChief]);

  const activeTiers = manual ? manualTiers : autoTiers;
  const showTutorial = manual ? true : autoTiers.length > 0 && !dismissedThisSession;

  // EE (Kapitel 21.7): nur vor dem AUTOMATISCHEN Trigger, nie beim manuellen "erneut anzeigen".
  const showIosHint = !manual && showTutorial && !dismissedIosHint && isIosNonStandalone();

  const deck = useMemo(() => buildDeck(activeTiers), [activeTiers]);

  // Beim Öffnen eines neuen Stapels immer bei Karte 0 starten.
  useEffect(() => {
    setIndex(0);
  }, [deck.length, showIosHint]);

  if (!user || !token) return null;
  if (!showTutorial) return null;

  async function finish() {
    if (!manual) {
      setSaving(true);
      try {
        const data = await apiFetch<{ user: AuthUser }>('/api/me/onboarding', {
          method: 'PUT',
          token,
          body: { tiers: activeTiers },
        });
        setUser(data.user);
        hapticSuccess();
      } catch {
        // Beim nächsten Login/Reload wird erneut versucht — kein blockierender Fehler-State,
        // damit ein Netzwerk-Hänger den Nutzer nicht im Tutorial gefangen hält.
      } finally {
        setSaving(false);
        setDismissedThisSession(true);
      }
    }
    onClose?.();
  }

  if (showIosHint) {
    return (
      <div
        className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]"
        role="dialog"
        aria-modal="true"
      >
        <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-5 w-full max-w-md border border-border/60 space-y-4">
          <h3 className="text-lg font-semibold">Für Benachrichtigungen</h3>
          <p className="text-sm text-slate-300">
            Füge ShiftSync zu deinem Home-Bildschirm hinzu, damit Erinnerungen und
            Push-Benachrichtigungen funktionieren.
          </p>
          <div className="bg-surface-3 rounded-lg p-3 space-y-2 text-sm text-slate-300">
            <p>
              1. Tippe unten in Safari auf{' '}
              <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-slate-700 align-middle">
                ⬆️
              </span>{' '}
              (Teilen).
            </p>
            <p>2. Wähle „Zum Home-Bildschirm".</p>
            <p>3. Öffne ShiftSync danach über das neue Icon.</p>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={() => setDismissedIosHint(true)} className="btn-ghost text-sm">
              Überspringen
            </button>
          </div>
        </div>
      </div>
    );
  }

  const card = deck[index];
  if (!card) return null;
  const isFirst = index === 0;
  const isLast = index === deck.length - 1;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-5 w-full max-w-md border border-border/60 space-y-4">
        <div className="flex items-center gap-1.5" aria-hidden>
          {deck.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full ${i <= index ? 'bg-brand' : 'bg-surface-3'}`}
            />
          ))}
        </div>
        <p className="text-xs text-slate-500">
          Karte {index + 1} von {deck.length}
        </p>
        <h3 className="text-lg font-semibold">{card.title}</h3>
        <ul className="space-y-1.5 text-sm text-slate-300 list-disc list-inside">
          {card.points.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
        <div className="flex items-center justify-between pt-1">
          <button type="button" onClick={() => void finish()} disabled={saving} className="btn-ghost text-sm disabled:opacity-50">
            Überspringen
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button type="button" onClick={() => setIndex((i) => i - 1)} className="btn-ghost text-sm">
                Zurück
              </button>
            )}
            <button
              type="button"
              onClick={() => (isLast ? void finish() : setIndex((i) => i + 1))}
              disabled={saving}
              className="btn text-sm disabled:opacity-50"
            >
              {isLast ? (saving ? '…' : 'Fertig') : 'Weiter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
